import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { LAB } from "@/data/lab";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/tests", label: "Tests" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1], delay: 0.2 }}
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-white/80 border-b border-slate-200/70"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-[92px]">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="flex items-center gap-3 group"
          onClick={() => setOpen(false)}
        >
          <img
            src={LAB.images.logo}
            alt="Lifeline Diagnostics"
            className="h-14 md:h-16 w-auto object-contain"
          />
          <span className="sr-only">Lifeline Diagnostics</span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `nav-link text-[13px] font-mono-tag ${
                  isActive ? "text-navy" : "text-ink/70 hover:text-navy"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <a
            href={LAB.phoneHref}
            data-testid="nav-phone-cta"
            className="inline-flex items-center gap-2 text-[13px] font-mono-tag text-white bg-navy px-4 py-2.5 rounded-none hover:bg-cyan-brand transition-colors duration-500"
          >
            <Phone className="h-3.5 w-3.5" strokeWidth={2.2} />
            {LAB.phone}
          </a>
          <a
            href={LAB.phone2Href}
            data-testid="nav-phone2-cta"
            className="inline-flex items-center gap-2 text-[13px] font-mono-tag text-navy border border-navy px-4 py-2.5 rounded-none hover:bg-navy hover:text-white transition-colors duration-500"
          >
            {LAB.phone2}
          </a>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-menu-toggle"
          className="md:hidden text-navy p-2 -mr-2"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.7, 0, 0.2, 1] }}
            className="md:hidden bg-white/95 backdrop-blur-xl border-t border-slate-200 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-link-mobile-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `font-display text-2xl ${
                      isActive ? "text-navy" : "text-ink/70"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={LAB.phoneHref}
                data-testid="nav-phone-cta-mobile"
                className="mt-3 inline-flex items-center gap-2 text-[13px] font-mono-tag text-white bg-navy px-4 py-3 self-start"
              >
                <Phone className="h-3.5 w-3.5" />
                {LAB.phone}
              </a>
              <a
                href={LAB.phone2Href}
                data-testid="nav-phone2-cta-mobile"
                className="inline-flex items-center gap-2 text-[13px] font-mono-tag text-navy border border-navy px-4 py-3 self-start"
              >
                <Phone className="h-3.5 w-3.5" />
                {LAB.phone2}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
